import { Badge } from "@/components/ui/badge"


interface SummaryItemProps {
  label: string
  children: React.ReactNode
}

function SummaryItem({ label, children }: SummaryItemProps) {
  return (
    <div className="space-y-1">
      <h3 className="font-medium text-sm text-muted-foreground">{label}</h3>
      <div className="text-sm">{children}</div>
    </div>
  )
}

interface WeightedListProps {
  items: Array<{ [key: string]: string | number }>
  labelKey: string
}

function WeightedList({ items, labelKey }: WeightedListProps) {
  return (
    <ul className="list-disc list-inside space-y-1">
      {items.map((item, index) => (
        <li key={index} className="text-sm">
          {item[labelKey]} ({(Number(item.weight) * 100).toFixed(1)}%)
        </li>
      ))}
    </ul>
  )
}

const US_STATUSES = ["US Citizen", "Green Card", "EAD", "Valid Visa with Work Rights"] as const
const CANADA_STATUSES = ["Canadian Citizen", "Permanent Resident", "Valid Work Permit"] as const

export type HiringCampaignFile={
    position:string,
    companyName:string,
    location: string,
    employmentType: string
    problemSpaces: Array<{ category: string; weight: number }>
    projectDescription: string
    mainSkills: string[]
    niceToHaveSkills: Array<{ skill: string; weight: number }>
    qualifications: {
      mustHave: string[]
      niceToHave: Array<{ qualification: string; weight: number }>
    }
    redFlags: string[],
    startDate:string,
    endDate:string,
    experienceLevel:string,
    workAuthorization: {
      requiresSponsorship: boolean
      acceptedStatuses:
        | "Any legal work authorization"
        | {
            US?: (typeof US_STATUSES)[number][]
            Canada?: (typeof CANADA_STATUSES)[number][]
            Other?: string
          }
      requiresSecurityClearance: boolean
      requiresCitizenshipForClearance?: boolean
    },
    locationFlexibility: string;
    role: {
      classification:{
      category: string
      roles: Array<{
        role: string
        description: string
        weight: number
      }>
      why: string
    }}

  }

type SummarySectionProps={
  data:HiringCampaignFile
}


export function SummarySection( {data} : SummarySectionProps) {
  const labelKey = "roleDescription"
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <SummaryItem label="Job Title">{data.position}</SummaryItem>
        <SummaryItem label="CompanyName">{data.companyName}</SummaryItem>
        <SummaryItem label="Location">{data.location}</SummaryItem>
        <SummaryItem label="Employment Type">{data.employmentType}</SummaryItem>
        <SummaryItem label="Start Date">{data.startDate}</SummaryItem>
        <SummaryItem label="End Date">{data.endDate}</SummaryItem>
        <SummaryItem label="Experience level">{data.experienceLevel}</SummaryItem>
        <SummaryItem label="Location Flexibility">{data.locationFlexibility}</SummaryItem>
      </div>
      <SummaryItem label="Category">{data.role.classification.category}</SummaryItem>

      <SummaryItem label="Roles">
        <WeightedList
          items={data.role.classification.roles.map((role) => ({
            ...role,
            [labelKey]: `${role.role}: ${role.description}`,
          }))}
          labelKey={labelKey}
        />
      </SummaryItem>

      <SummaryItem label="Analysis">{data.role.classification.why}</SummaryItem>

      <div className="space-y-4">
        <SummaryItem label="Main Skills">
          <ul className="list-disc list-inside">
            {data.mainSkills.map((skill, index) => (
              <li key={index} className="text-sm">
                {skill}
              </li>
            ))}
          </ul>
        </SummaryItem>

        <SummaryItem label="Nice-to-Have Skills">
          <WeightedList items={data.niceToHaveSkills} labelKey="skill" />
        </SummaryItem>
      </div>

      <div className="space-y-4">
        <SummaryItem label="Must-Have Qualifications">
          <ul className="list-disc list-inside">
            {data.qualifications.mustHave.map((qual, index) => (
              <li key={index} className="text-sm">
                {qual}
              </li>
            ))}
          </ul>
        </SummaryItem>

        <SummaryItem label="Nice-to-Have Qualifications">
          <WeightedList items={data.qualifications.niceToHave} labelKey="qualification" />
        </SummaryItem>
      </div>

      {/* <SummaryItem label="Red Flags">
        <ul className="list-disc list-inside">
          {data.redFlags.map((flag, index) => (
            <li key={index} className="text-sm">
              {flag}
            </li>
          ))}
        </ul>
      </SummaryItem> */}
      <SummaryItem label="Work Authorization Requirements">
        <div className="space-y-2">
          <div>
            <span className="font-medium">Sponsorship Required:</span>{" "}
            {data.workAuthorization.requiresSponsorship ? "Yes" : "No"}
          </div>
          <div>
            <span className="font-medium">Accepted Statuses:</span>{" "}
            {typeof data.workAuthorization.acceptedStatuses === "string" ? (
              <span>{data.workAuthorization.acceptedStatuses}</span>
            ) : (
              <ul className="list-disc list-inside">
                {data.workAuthorization.acceptedStatuses.US && (
                  <li>
                    US:{" "}
                    {data.workAuthorization.acceptedStatuses.US.map((status, index) => (
                      <Badge key={index} variant="secondary" className="mr-1">
                        {status}
                      </Badge>
                    ))}
                  </li>
                )}
                {data.workAuthorization.acceptedStatuses.Canada && (
                  <li>
                    Canada:{" "}
                    {data.workAuthorization.acceptedStatuses.Canada.map((status, index) => (
                      <Badge key={index} variant="secondary" className="mr-1">
                        {status}
                      </Badge>
                    ))}
                  </li>
                )}
                {data.workAuthorization.acceptedStatuses.Other && (
                  <li>Other: {data.workAuthorization.acceptedStatuses.Other}</li>
                )}
              </ul>
            )}
          </div>
        </div>
      </SummaryItem>
    </div>
  )
}

